import React from "react";
import {
  CheckIcon,
  ArrowRight,
  Calendar,
  Clock,
  Tag,
  StarIcon,
  BadgeCheck,
  ExternalLink,
  Users,
  ArrowUpRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Link } from "react-router-dom";
import { useCursor } from "@/context/CursorContext";

const getGradientClass = (color) => {
  const gradients = {
    blue: "from-blue-500/30 to-blue-500/30",
    green: "from-green-500/30 to-green-500/30",
    purple: "from-purple-500/30 to-purple-500/30",
    pink: "from-pink-500/30 to-pink-500/30",
    orange: "from-orange-500/30 to-orange-500/30",
    yellow: "from-yellow-500/30 to-yellow-500/30",
  };

  return gradients[color] || gradients.blue;
};

const getTextColorClass = (color) => {
  const colors = {
    blue: "text-blue-500",
    green: "text-green-500",
    purple: "text-purple-500",
    pink: "text-pink-500",
    orange: "text-orange-500",
    yellow: "text-yellow-500",
  };

  return colors[color] || colors.blue;
};

const buttonStyles = {
  default: cn(
    "h-12 bg-white dark:bg-zinc-900",
    "hover:bg-zinc-50 dark:hover:bg-zinc-800",
    "text-zinc-900 dark:text-zinc-100",
    "border border-zinc-200 dark:border-zinc-800",
    "hover:border-zinc-300 dark:hover:border-zinc-700",
    "shadow-sm hover:shadow-md",
    "text-sm font-medium"
  ),
  highlight: cn(
    "h-12 bg-zinc-900 dark:bg-zinc-100",
    "hover:bg-zinc-800 dark:hover:bg-zinc-300",
    "text-white dark:text-zinc-900",
    "shadow-[0_1px_15px_rgba(0,0,0,0.1)]",
    "hover:shadow-[0_1px_20px_rgba(0,0,0,0.15)]",
    "font-medium text-sm"
  ),
};

const badgeStyles = cn(
  "px-4 py-1.5 text-sm font-medium",
  "bg-zinc-900 dark:bg-zinc-100",
  "text-white dark:text-zinc-900",
  "border-none shadow-lg"
);

/**
 * Universal Card Component
 *
 * This component is designed to be used in various sections:
 * - Services, Features (default view)
 * - Blog (with image header, tags, date)
 * - Testimonials (with quote, stars, avatar)
 * - Certificates (with image, badge, skills)
 * - WorkProcess (with id number, icon, details list)
 * - About (with icon, title, description)
 *
 * @param {Object} item - The item data to display in the card
 * @param {number} index - The index of the item in the list
 * @param {string} variant - Card variant: 'default', 'blog', 'testimonial', 'certificate', 'process', 'about'
 */
const Card = ({ item, index, variant = "default" }) => {
  const { setCursorVariant, setCursorText } = useCursor();

  const handleEnter = () => {
    setCursorVariant("project");
    setCursorText("Visit");
  };

  const handleLeave = () => {
    setCursorVariant("default");
    setCursorText("");
  };

  // Common card classes for all variants
  const cardClasses = cn(
    "group relative p-4 rounded-md transition-all duration-300 h-full w-full",
    "border border-white/10 bg-background",
    "hover:shadow-[0_2px_12px_rgba(0,0,0,0.03)] dark:hover:shadow-[0_2px_12px_rgba(255,255,255,0.03)]",
    "hover:-translate-y-0.5 will-change-transform",
    {
      "shadow-[0_2px_12px_rgba(0,0,0,0.03)] ": item.hasPersistentHover,
      "dark:shadow-[0_2px_12px_rgba(255,255,255,0.03)]":
        item.hasPersistentHover,
    }
  );

  // Background pattern effect
  const backgroundPattern = (
    <div
      className={`absolute inset-0 ${
        item.hasPersistentHover
          ? "opacity-100"
          : "opacity-0 group-hover:opacity-100"
      } transition-opacity duration-300`}
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(0,0,0,0.02)_1px,transparent_1px)] dark:bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[length:4px_4px]" />
    </div>
  );

  // Gradient border effect
  const gradientBorder = (
    <div
      className={`absolute inset-0 -z-10 rounded-xl p-px bg-gradient-to-br from-transparent via-gray-100/50 to-transparent dark:via-white/10 ${
        item.hasPersistentHover
          ? "opacity-100"
          : "opacity-0 group-hover:opacity-100"
      } transition-opacity duration-300`}
    />
  );

  // Render different variants
  switch (variant) {
    // Case Study Card
    case "case-study":
      return (
        <div
          className={cardClasses}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
        >
          <Link to={item.slug ? `/case-study/${item.slug}` : "#"}>
            {backgroundPattern}
            {item.mainImage && (
              <div className="relative aspect-[16/9] -m-4 mb-4 overflow-hidden rounded-t-md">
                <img
                  src={item.mainImage}
                  alt={item.title}
                  className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent opacity-60" />

                {/* Category badge */}
                {item.category && (
                  <div className="absolute top-4 left-4 px-3 py-1 rounded-full text-xs font-medium bg-background/80 backdrop-blur-sm">
                    {item.category}
                  </div>
                )}
              </div>
            )}
            <div className="space-y-4">
              {/* Technologies */}
              {item.technologies && (
                <div className=" flex items-center justify-between">
                  <div className="flex flex-nowrap overflow-hidden">
                    {item.technologies.slice(0, 2).map((tech, i) => (
                      <span
                        key={i}
                        className="text-xs px-2 py-1 bg-accent/10 rounded-md mr-2"
                      >
                        {tech}
                      </span>
                    ))}
                    {item.technologies.length > 2 && (
                      <span className="text-xs px-2 py-1 bg-accent/10 rounded-md">
                        +{item.technologies.length - 2}
                      </span>
                    )}
                  </div>
                </div>
              )}
              <h3 className="text-2xl font-medium group-hover:text-primary transition-colors">
                {item.title}
              </h3>

              <p className="text-muted-foreground line-clamp-2">
                {item.description}
              </p>

              {/* Project details */}
              <div className="flex flex-wrap gap-3 pt-2">
                {item.date && (
                  <div
                    className={cn(
                      "flex items-center gap-1 px-2 py-1 rounded-md text-xs",
                      item.backgroundColor || "bg-primary/5",
                      item.textColor || "text-primary"
                    )}
                  >
                    <Calendar className="h-3 w-3" />
                    <span>{item.date}</span>
                  </div>
                )}
                {item.client && (
                  <div
                    className={cn(
                      "flex items-center gap-1 px-2 py-1 rounded-md text-xs",
                      item.backgroundColor || "bg-primary/5",
                      item.textColor || "text-primary"
                    )}
                  >
                    <Users className="h-3 w-3" />
                    <span>{item.client}</span>
                  </div>
                )}
              </div>
            </div>

            {gradientBorder}
          </Link>
        </div>
      );

    // Blog Card
    case "blog":
      return (
        <div
          className={cardClasses}
          onMouseEnter={handleEnter}
          onMouseLeave={handleLeave}
        >
          <Link to={item.slug ? `/blog/${item.slug}` : "#"}>
            {backgroundPattern}
            {item.mainImage && (
              <div className="relative h-48 -m-4 mb-4 overflow-hidden rounded-t-md">
                <img
                  src={item.mainImage}
                  alt={item.title}
                  className="object-cover w-full h-full transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-background/90 to-transparent opacity-70" />
              </div>
            )}
            <div className="space-y-4">
              {item.tags && (
                <div className="flex overflow-hidden mb-3">
                  {item.tags.slice(0, 2).map((tag, i) => (
                    <span
                      key={i}
                      className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-800 dark:text-zinc-200 gap-1 mr-2"
                    >
                      {tag}
                    </span>
                  ))}
                  {item.tags.length > 2 && (
                    <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-800 dark:text-zinc-200">
                      +{item.tags.length - 2}
                    </span>
                  )}
                </div>
              )}

              <h3 className="font-medium text-xl mb-2 group-hover:text-primary transition-colors">
                {item.title}
              </h3>

              <p className="text-muted-foreground text-sm">
                {item.excerpt || item.description}
              </p>

              {(item.date || item.readTime) && (
                <div className="flex items-center justify-between mt-auto pt-3 border-t border-white/10">
                  {item.date && (
                    <div className="inline-flex items-center text-xs text-muted-foreground gap-1">
                      <Calendar className="w-3 h-3" />
                      {item.date}
                    </div>
                  )}
                  {item.readTime && (
                    <div className="inline-flex items-center text-xs text-muted-foreground gap-1">
                      <Clock className="w-3 h-3" />
                      {item.readTime}
                    </div>
                  )}
                </div>
              )}
            </div>

            {gradientBorder}
          </Link>
        </div>
      );

    // Testimonial Card
    case "testimonial":
      return (
        <div className={cardClasses}>
          {backgroundPattern}
          <div className="flex flex-col space-y-4">
            {item.stars && (
              <div className="flex items-center space-x-1">
                {Array(item.stars)
                  .fill(null)
                  .map((_, i) => (
                    <StarIcon
                      key={i}
                      className="w-5 h-5 fill-yellow-400 text-yellow-400"
                    />
                  ))}
              </div>
            )}

            <blockquote className="text-muted-foreground font-[425] text-sm leading-relaxed">
              "{item.quote}"
            </blockquote>

            <div className="flex items-center pt-4 mt-auto">
              {item.image && (
                <div className="relative w-10 h-10 rounded-full overflow-hidden mr-4 border border-white/10">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="object-cover w-full h-full"
                  />
                </div>
              )}
              <div>
                <p className="font-medium text-sm">{item.name}</p>
                <p className="text-xs text-muted-foreground">
                  {item.position}
                  {item.company && `, ${item.company}`}
                </p>
              </div>
            </div>
          </div>
          {gradientBorder}
        </div>
      );

    // Certificate Card
    case "certificate":
      return (
        <div className={cardClasses}>
          {backgroundPattern}
          <div className="gap-4 flex flex-col h-full">
            {item.icon && (
              <div className="relative flex items-center justify-center w-14 h-14 rounded-xl bg-zinc-100 dark:bg-zinc-800">
                <div
                  className={`absolute inset-0 bg-gradient-to-r ${getGradientClass(
                    item.color
                  )} blur-xl rounded-full`}
                />
                {item.icon && (
                  <div
                    className={`relative z-10 ${getTextColorClass(item.color)}`}
                  >
                    {item.icon}
                  </div>
                )}
              </div>
            )}

            <div>
              <h3 className="font-medium text-xl">{item.title}</h3>

              <p className="text-muted-foreground text-sm mb-4">
                {item.description}
              </p>
            </div>

            {item.issuer && (
              <div className="flex-1 grid grid-cols-2 gap-4 mb-4">
                <div>
                  <p className="text-xs text-muted-foreground">Issuer</p>
                  <p className="text-sm font-medium">{item.issuer}</p>
                </div>
                {item.instructor && (
                  <div>
                    <p className="text-xs text-muted-foreground">Instructor</p>
                    <p className="text-sm font-medium">{item.instructor}</p>
                  </div>
                )}
                {item.date && (
                  <div>
                    <p className="text-xs text-muted-foreground">Date</p>
                    <p className="text-sm font-medium">{item.date}</p>
                  </div>
                )}
                {item.hours && (
                  <div>
                    <p className="text-xs text-muted-foreground">Duration</p>
                    <p className="text-sm font-medium">{item.hours}</p>
                  </div>
                )}
              </div>
            )}

            {item.certificateId && (
              <div className="flex justify-between items-center pt-3 border-t border-white/10">
                <div className="text-xs">
                  <p className="text-muted-foreground">Certificate ID:</p>
                  <p className="font-mono">{item.certificateId}</p>
                </div>
                {item.certificateUrl && (
                  <Button variant="ghost" size="sm" className="group" asChild>
                    <Link
                      to={`https://${item.certificateUrl}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1  transition-transform group-hover:translate-x-0.5"
                    >
                      Verify
                      <ExternalLink className="h-3 w-3" />
                    </Link>
                  </Button>
                )}
              </div>
            )}
          </div>
          {gradientBorder}
        </div>
      );

    // Default Card (Services, Features, etc.)
    default:
      return (
        <div className={cardClasses}>
          {backgroundPattern}

          {item.badge && (
            <div className="absolute -top-4 right-6">
              <Badge className={badgeStyles}>{item.badge}</Badge>
            </div>
          )}

          <div className="flex flex-col space-y-3">
            <div className="flex-shrink-0 flex items-center justify-between">
              {(item.icon || item.id) && (
                <div className="relative flex items-center justify-center w-14 h-14 rounded-xl bg-zinc-100 dark:bg-zinc-800">
                  <div
                    className={`absolute inset-0 bg-gradient-to-r ${getGradientClass(
                      item.color
                    )} blur-xl rounded-full`}
                  />
                  {item.icon && (
                    <div
                      className={`relative z-10 ${getTextColorClass(
                        item.color
                      )}`}
                    >
                      {item.icon}
                    </div>
                  )}
                  {item.id && (
                    <div className="absolute -top-3 -left-3 w-8 h-8 rounded-full bg-background flex items-center justify-center border border-white/10 font-medium text-sm">
                      {item.id}
                    </div>
                  )}
                  {item.status && (
                    <span
                      className={cn(
                        "text-xs font-medium px-2 py-1 rounded-lg backdrop-blur-sm",
                        "bg-black/5 dark:bg-white/10 text-gray-600 dark:text-gray-300",
                        "transition-colors duration-300 group-hover:bg-black/10 dark:group-hover:bg-white/20"
                      )}
                    >
                      {item.status}
                    </span>
                  )}
                </div>
              )}

              {item.price && (
                <span className="flex gap-1 items-end">
                  <div className="flex gap-2 items-center">
                    <div className="flex flex-wrap items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                      <span className="px-2 py-1 rounded-md bg-black/5 dark:bg-white/10 backdrop-blur-sm transition-all duration-200 hover:bg-black/10 dark:hover:bg-white/20">
                        Starting at
                      </span>
                    </div>

                    <span className="text-xl font-medium text-zinc-900 dark:text-zinc-100">
                      {item.price}
                    </span>
                  </div>
                  <span className="text-md font-normal">PLN</span>
                </span>
              )}
            </div>

            <div className="space-y-4">
              <div className="space-y-2">
                <h3 className="font-medium text-gray-900 dark:text-gray-100 tracking-tight text-lg">
                  {item.title}
                  {item.meta && (
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 font-normal">
                      {item.meta}
                    </span>
                  )}
                </h3>

                {item.description && (
                  <p className="text-sm text-muted-foreground leading-snug font-[425]">
                    {item.description}
                  </p>
                )}

                {item.descriptions && (
                  <div className="space-y-1">
                    {item.descriptions.map((desc, i) => (
                      <p
                        key={i}
                        className="text-muted-foreground text-sm leading-snug font-[425]"
                      >
                        {desc}
                      </p>
                    ))}
                  </div>
                )}
              </div>

              {item.details && (
                <div className="flex flex-col gap-2">
                  {item.details.map((detail, i) => (
                    <p
                      key={i}
                      className="text-muted-foreground text-sm leading-snug font-[425]"
                    >
                      {detail}
                    </p>
                  ))}
                </div>
              )}

              {item.features && (
                <div className="space-y-4">
                  {item.features?.map((feature) => (
                    <div key={feature.name} className="flex gap-4">
                      <div className="mt-1 p-0.5 rounded-full text-emerald-600 dark:text-emerald-400">
                        <CheckIcon className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                          {feature.name}
                        </div>
                        <div className="text-sm text-zinc-500 dark:text-zinc-400">
                          {feature.description}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {item.tags && (
              <div className="flex overflow-hidden mb-3">
                {item.tags.slice(0, 2).map((tag, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-800 dark:text-zinc-200 gap-1 mr-2"
                  >
                    <Tag className="w-3 h-3" />
                    {tag}
                  </span>
                ))}
                {item.tags.length > 2 && (
                  <span className="inline-flex items-center px-2 py-1 text-xs rounded-full bg-zinc-100 dark:bg-zinc-800 text-zinc-800 dark:text-zinc-200">
                    +{item.tags.length - 2}
                  </span>
                )}
              </div>
            )}
          </div>

          {item.cta && (
            <div className="pt-6 mt-auto">
              <Button
                asChild
                className={cn(
                  "w-full relative transition-all duration-300",
                  item.highlight ? buttonStyles.highlight : buttonStyles.default
                )}
              >
                <a href="#contact" className="group">
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {item.ctaText || "Request now"}
                    <ArrowRight
                      className="-me-2 ms-0 transition-transform group-hover:translate-x-0.5"
                      size={16}
                      strokeWidth={2}
                      aria-hidden="true"
                    />
                  </span>
                </a>
              </Button>
            </div>
          )}

          {gradientBorder}
        </div>
      );
  }
};

export { Card };
